// ---------------------------------------------------------------------------
// Audit Log - timeline entry row
//
// Renders a single audit log entry in the timeline view, with expandable
// detail and raw JSON panels.
// ---------------------------------------------------------------------------

import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import type { AuditLogEntry } from "@/lib/admin-api/audit-log";
import {
  formatFullDate,
  getActionLabel,
  getActionStyle,
  getResourceLabel,
  relativeTime,
} from "./formatters";
import { ChangeDetails } from "./ChangeDetails";
import { DetailJsonView } from "./DetailJsonView";

interface TimelineEntryProps {
  readonly entry: AuditLogEntry;
}

export function TimelineEntry({ entry }: TimelineEntryProps) {
  const [expanded, setExpanded] = useState(false);
  const [showJson, setShowJson] = useState(false);
  const hasChanges = entry.before_data != null || entry.after_data != null;

  const style = getActionStyle(entry.action);
  const Icon = style.icon;

  const userName = entry.admin_user?.full_name ?? "Administrateur inconnu";
  const metadata = entry.metadata as Record<string, unknown> | null;
  const ip = metadata?.ip as string | undefined;
  const department = metadata?.department as string | undefined;

  return (
    <div className="flex gap-3">
      {/* Icon */}
      <div className="flex shrink-0 flex-col items-center">
        <div
          className={`flex h-8 w-8 items-center justify-center rounded-full ${style.bgColor}`}
        >
          <Icon className={`h-4 w-4 ${style.iconColor}`} />
        </div>
        <div className="w-px flex-1 bg-stone-200" />
      </div>

      {/* Content */}
      <div className="min-w-0 flex-1 pb-6">
        {/* Top row: user, action, resource, time */}
        <div className="flex flex-wrap items-baseline gap-x-2">
          <span className="text-sm font-medium text-stone-900">{userName}</span>
          <span
            className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${style.badgeBg} ${style.badgeText}`}
          >
            {getActionLabel(entry.action)}
          </span>
          <span className="text-sm text-stone-600">
            {getResourceLabel(entry.resource)}
          </span>
          {entry.resource_id && (
            <span className="font-mono text-xs text-stone-400">
              {entry.resource_id}
            </span>
          )}
        </div>

        {/* Second row: meta info */}
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="text-xs text-stone-400" title={formatFullDate(entry.created_at)}>
            {relativeTime(entry.created_at)}
          </span>
          {department && (
            <span className="inline-flex items-center rounded bg-stone-100 px-1.5 py-0.5 text-xs text-stone-500">
              {department}
            </span>
          )}
          {ip && (
            <span className="font-mono text-xs text-stone-400">{ip}</span>
          )}
        </div>

        {/* Expand controls */}
        <div className="mt-1.5 flex items-center gap-3">
          {hasChanges && (
            <button
              onClick={() => { setExpanded((prev) => !prev); setShowJson(false); }}
              className="flex items-center gap-1 text-xs font-medium text-stone-500 hover:text-stone-700"
            >
              {expanded ? (
                <ChevronDown className="h-3.5 w-3.5" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5" />
              )}
              {expanded ? "Masquer les details" : "Voir les details"}
            </button>
          )}
          <button
            onClick={() => { setShowJson((prev) => !prev); setExpanded(false); }}
            className="flex items-center gap-1 text-xs font-medium text-stone-400 hover:text-stone-600"
          >
            {showJson ? "Masquer JSON" : "JSON"}
          </button>
        </div>

        {/* Change details */}
        {expanded && hasChanges && (
          <div className="mt-2 rounded-lg border border-stone-200 bg-stone-50 p-3">
            <ChangeDetails before={entry.before_data} after={entry.after_data} />
          </div>
        )}

        {/* Full JSON view */}
        {showJson && (
          <div className="mt-2">
            <DetailJsonView entry={entry} />
          </div>
        )}
      </div>
    </div>
  );
}
